import React, { useEffect, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { bff } from '@/lib/http/bff';
import AudioStemRow from './AudioStemRow';
import MixTransport from './MixTransport';
import UiSlider from './ui/UiSlider';
import { gainToDb, meterFromDb } from '../lib/audioMath';
// import YoutubeSplitPanel from './YoutubeSplitPanel';
import UploadMp3Panel from './UploadMp3Panel';
import useAudioMixEngine from '../hooks/useAudioMixEngine';
import SearchBox from '@/shared/components/ui/SearchBox';
// NEW: stabilize time
import useStableTransportTime from '../hooks/useStableTransportTime';
// NEW: preparing hook + overlay
import usePlayerPreparingState from '../hooks/usePlayerPreparingState';
import PlayerLoadingOverlay from './PlayerLoadingOverlay';
// NEW: autoplay guard (moved out)
import useAutoplayGuard from '../hooks/useAutoplayGuard';

// FIX: restore missing imports used below
import MninimalisticList from '@/shared/components/ui/MninimalisticList';
import MasterFrequencyResponse from './MasterFrequencyResponse';
import WaveformPlot from './WaveformPlot';
import MasterVuPro from './MasterVuPro';
import { clearAutoplayTimers } from '../hooks/stateGuards';
import { L } from '../utils/logger';

// Small helper: detect if viewport is below the md breakpoint (< 768px)
function useIsSmallScreen() {
  const get = () => (typeof window !== 'undefined') && window.matchMedia('(max-width: 767.98px)').matches;
  const [isSmall, setIsSmall] = useState<boolean>(get());
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const mq = window.matchMedia('(max-width: 767.98px)');
    const handler = (e: MediaQueryListEvent) => setIsSmall(e.matches);
    try { mq.addEventListener('change', handler); } catch { /* Safari */ try { /* @ts-ignore */ mq.addListener(handler); } catch {} }
    return () => { try { mq.removeEventListener('change', handler); } catch { /* Safari */ try { /* @ts-ignore */ mq.removeListener(handler); } catch {} } };
  }, []);
  return isSmall;
}

interface LibraryRow { id_scope: string|number; artist?: string; song?: string; album?: string }
interface LibraryRes { ok: boolean; rows: LibraryRow[] }
// Removed: FilesRes (unused)
interface Track { role: string; url: string; expiresAt?: number }
interface TracksRes { ok: boolean; id_scope: string|number; tracks: Track[]; warning?: string }
function fetchLibrary(q?: string): Promise<LibraryRes> {
  const ts = Date.now();
  const qp = new URLSearchParams({ _ts: String(ts) });
  if (q && q.trim()) qp.set('q', q.trim());
  return bff.get(`/services/media/audio-library?${qp.toString()}`);
}
// Removed: fetchFiles (unused)
function fetchTracks(id_scope: string|number): Promise<TracksRes> { return bff.get(`/services/media/audio-library/${id_scope}/tracks`); }

// Polished VU floor/ceiling — shared via audioMath.meterFromDb(db, floor, ceil)
const VU_FLOOR = -70;
const VU_CEIL = 0;

const AudioLibraryPlayer: React.FC = () => {
  const qc = useQueryClient();
  const [selected, setSelected] = useState<LibraryRow | null>(null);
  const isSmall = useIsSmallScreen();
  // Mobile tab: 'library' or 'player'. Default to player if a selection exists, otherwise library.
  const [activeTab, setActiveTab] = useState<'library'|'player'>(() => 'library');
  const [search, setSearch] = useState('');

  const libQuery = useQuery({
    queryKey:['audioLibrary', search],
    queryFn: () => fetchLibrary(search),
    // Keep a modest refetch to stay fresh while typing; react-query will refetch on key change
    refetchInterval: 30000,
  });
  // Removed: filesQuery (unused)
  const tracksQuery = useQuery({ queryKey:['audioLibraryTracks', selected?.id_scope], queryFn: () => fetchTracks(selected!.id_scope), enabled: !!selected });

  // Mix engine wiring for current tracks
  const useBufferMode = true;
  const mix = useAudioMixEngine({ tracks: (tracksQuery.data?.tracks || []).map(t => ({ role: t.role, url: t.url })), bufferMode: useBufferMode });

  // NEW: stabilized current time for seek bar; reset on scope change
  const stableTime = useStableTransportTime(mix as any, { resetKey: selected?.id_scope });
  // NEW: autoplay guard (auto-arm gesture if blocked or too slow)
  useAutoplayGuard(mix, selected?.id_scope ?? null, tracksQuery.data?.tracks);
  // NEW: preparing state (tracks loading or prefetching buffers) + playback pending
  const { preparing, showOverlay, progress } = usePlayerPreparingState({
    scopeId: selected?.id_scope ?? null,
    tracks: tracksQuery.data?.tracks,
    tracksLoading: tracksQuery.isLoading,
    mix
  });

  // NEW: overlay when nothing is selected yet
  const noSelection = !selected;
  // NEW: single flag to control overlay + scroll lock on the Player frame
  const overlayActive = noSelection || (!!selected && showOverlay);

  // Removed user-facing autoplay prompt; we'll silently resume on first gesture if blocked

  const [uiSpeed, setUiSpeed] = useState(1.0);
  const [uiPitchSt, setUiPitchSt] = useState(0);

  // Do NOT auto-select on desktop/tablet either: match phone behavior.
  // Start in Library and wait for the user to pick a song.

  // Live refresh handled by panels via job-progress completion; no WS subscription here.

  // On component unmount, clear any autoplay timers that might still be around
  useEffect(() => {
    return () => { try { clearAutoplayTimers(); } catch {} };
  }, []);

  // When scope changes, notify engine
  useEffect(() => {
    if (!selected?.id_scope) return;
    L.info('media:scope', 'select', { scope: String(selected.id_scope) });
    mix.onScopeChange();
  }, [selected?.id_scope]);

  // Initialize UI pitch from engine on mount/scope change
  useEffect(() => {
    const st = Number((mix as any).dsp?.pitch?.getSemitones?.() ?? 0);
    if (Number.isFinite(st)) setUiPitchSt(Math.max(-12, Math.min(12, st)));
  L.info('media:transport', 'uiInitFromEngine.pitch', { st });
  }, [selected?.id_scope]);


  // Re-apply last pitch to all stems whenever new tracks for the current scope are wired
  useEffect(() => {
    const tracks = tracksQuery.data?.tracks || [];
    if (!tracks.length) return;
    const scale = Math.pow(2, uiPitchSt / 12);
    try { (mix as any).dsp?.setRbPitch?.('*', scale); } catch {}
  }, [tracksQuery.data?.tracks?.length, uiPitchSt]);

  // dBFS periodic diagnostics removed

  // dBFS meters and UI removed

  const play = mix.play;
  const handlePlay = async () => {
    // Always resume AudioContext on explicit user gesture before transport ops
    try {
      const { getCtx } = await import('../hooks/mixEngine/ctxSingleton');
      const ctx = getCtx();
      if (ctx.state === 'suspended') { try { await ctx.resume(); } catch {} }
    } catch {}
    try { await (mix as any).preflightResetIfNeeded?.(); } catch {}
    await play();
  };
  const pause = mix.pause;
  const stop = mix.stop;
  const seekAll = (time: number) => { void mix.seek(time, true); };

  const rowClick = (row: LibraryRow) => {
    setSelected(row);
    // Removed: invalidate 'audioLibraryFiles' (no longer queried here)
    qc.invalidateQueries({ queryKey:['audioLibraryTracks', row.id_scope] });
  };

  // Sync mobile tab with selection (if a track is selected and we are on small, default to player)
  useEffect(() => {
    if (isSmall) {
      if (selected && activeTab !== 'player') setActiveTab('player');
      if (!selected && activeTab !== 'library') setActiveTab('library');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSmall, selected?.id_scope]);

  const LibraryFrame = (
    <div className="w-full md:w-3/5 rounded-xl border border-neutral-700/40 pac-glass p-4 flex flex-col h-full min-h-0 overflow-hidden">
        <div className="flex items-center justify-between mb-2 gap-3">
          <h2 className="text-sm font-semibold text-neutral-300">Library</h2>
          <div className="flex-1 flex justify-end">
            <SearchBox
              value={search}
              onChange={setSearch}
              placeholder="Search…"
              size="sm"
              className="max-w-[14rem]"
            />
          </div>
        </div>
        <div className="flex-1 overflow-auto pr-1">
          {libQuery.isLoading && <div className="text-xs text-neutral-500">Loading…</div>}
          {libQuery.isError && <div className="text-xs text-rose-500">Load failed</div>}
          {libQuery.isSuccess && libQuery.data.rows.length === 0 && (
            <div className="text-neutral-500 text-xs">No audio metadata yet</div>
          )}
          {libQuery.isSuccess && libQuery.data.rows.length > 0 && (() => {
            const rows = libQuery.data.rows;
            // Client-side filter remains as a safety for small lists or if server filtering is unavailable
            const q = search.trim().toLowerCase();
            const filtered = q
              ? rows.filter(r => {
                  const artist = (r.artist ?? '').toString().toLowerCase();
                  const song = (r.song ?? '').toString().toLowerCase();
                  const album = (r.album ?? '').toString().toLowerCase();
                  const scope = String(r.id_scope).toLowerCase();
                  return artist.includes(q) || song.includes(q) || album.includes(q) || scope.includes(q);
                })
              : rows;
            return (
            <MninimalisticList
              items={filtered}
              getKey={(r: LibraryRow) => String(r.id_scope)}
              renderText={(r: LibraryRow) => {
                const artist = (r.artist || '').trim();
                const song = (r.song || '').trim();
                const album = (r.album || '').trim();
                let text = '';
                if (artist && song) text = `${artist} – ${song}`;
                else if (song) text = song;
                else if (artist) text = artist;
                else text = String(r.id_scope);
                if (album) text += ` (${album})`;
                return text;
              }}
              isSelected={(r: LibraryRow) => !!(selected && r.id_scope === selected.id_scope)}
              onItemClick={(r: LibraryRow) => rowClick(r)}
              size="sm"
            />
            );
          })()}
        </div>
        <div className="mt-3 pt-3 border-t border-neutral-700/40">
          <UploadMp3Panel />
        </div>
      </div>
  );

  const PlayerFrame = (
      <div
        className={`w-full md:w-2/5 rounded-xl border border-neutral-700/40 p-6 pac-glass ${overlayActive ? 'overflow-hidden' : 'overflow-auto'} flex flex-col h-full relative`}
      >
        <div className="flex items-center justify-between mb-2">
          <h2 className="text-sm font-semibold text-neutral-300">Player</h2>
        </div>
        {/* UPDATED: show overlay with minimal subtext and progress while preparing */}
        <PlayerLoadingOverlay
          show={overlayActive}
          text={
            noSelection
              ? 'Select a song'
              : preparing
                ? 'Loading audio…'
                : 'Starting…'
          }
          subText={
            noSelection || !preparing
              ? undefined
              : (() => {
                  const name = (progress?.current || '').toString();
                  if (!name) return undefined;
                  // Remove extension (last .ext) before truncation
                  const base = name.replace(/\.[^./\\\s?]+$/, '');
                  return base.length > 60 ? `${base.slice(0, 57)}…` : base;
                })()
          }
          progressPct={
            noSelection || !preparing
              ? undefined
              : (() => {
                  const total = progress?.total ?? 0;
                  const done = progress?.done ?? 0;
                  return total > 0 ? (done / total) * 100 : 0;
                })()
          }
        />
        {/* Small warning for stem load failures with retry */}
        {(() => {
          try {
            const errs = (mix as any)?.stemErrors as Record<string, { status?: number; message: string; url?: string }> | null | undefined;
            if (!errs || Object.keys(errs).length === 0) return null;
            const firstKey = Object.keys(errs)[0];
            const e = errs[firstKey];
            const is404 = typeof e?.status === 'number' && e.status === 404;
            const is5xx = typeof e?.status === 'number' && e.status >= 500 && e.status < 600;
            const msg = is404
              ? 'Audio stems are no longer available in storage.'
              : is5xx ? `Server error ${e.status} while loading "${firstKey}".` : `Error loading "${firstKey}".`;
            const borderCls = is404 ? 'border-red-700/40 bg-red-900/30 text-red-300' : 'border-amber-700/40 bg-amber-900/30 text-amber-300';
            return (
              <div className={`mb-3 text-[11px] flex items-center justify-between gap-3 rounded border ${borderCls} px-3 py-2`}>
                <span className="truncate" title={e?.message || msg}>{msg}</span>
                <div className="shrink-0 flex items-center gap-2">
                  {!is404 && (
                    <button
                      className="px-2 py-1 rounded bg-amber-600/80 hover:bg-amber-600 text-black text-[11px]"
                      onClick={() => { try { (mix as any)?.retryFailed?.(); } catch { try { mix.onScopeChange(); } catch {} } }}
                    >Retry</button>
                  )}
                  <button
                    className={`px-2 py-1 rounded border text-[11px] ${is404 ? 'border-red-700/60 text-red-300 hover:text-red-100' : 'border-amber-700/60 text-amber-300 hover:text-amber-100'}`}
                    onClick={() => { try { (mix as any)?.clearErrors?.(); } catch {} }}
                  >Dismiss</button>
                </div>
              </div>
            );
          } catch { return null; }
        })()}
        {process.env.NODE_ENV === 'development' && (
          <pre className="text-[10px] text-neutral-400 max-h-24 overflow-auto my-2">
            {JSON.stringify((window as any).__LOGS__?.slice(-10), null, 2)}
          </pre>
        )}
        {selected && (() => {
          const sel = selected as LibraryRow;
          const artist = (sel.artist || '').trim();
          const song = (sel.song || '').trim();
          const album = (sel.album || '').trim();
          let text = '';
          if (artist && song) text = `${artist} – ${song}`;
          else if (song) text = song;
          else if (artist) text = artist;
          else text = '';
          if (album) text += ` (${album})`;
          return text ? (<div className="text-xs text-neutral-400 mb-2">{text}</div>) : null;
        })()}

        {tracksQuery.isLoading && <div className="text-xs text-neutral-500">Preparing tracks…</div>}
        {tracksQuery.isSuccess && tracksQuery.data.tracks.length === 0 && (
          <div className="text-xs text-neutral-500">
            No tracks for this scope
            {tracksQuery.data.warning === 'upstream_422' && ' (upstream 422)'}
            {tracksQuery.data.warning === 'upstream_422_fallback' && ' (retried broad fetch)'}
            {tracksQuery.data.warning === 'upstream_422_fallback_failed' && ' (fallback failed)'}
            .
          </div>
        )}

        {tracksQuery.isSuccess && tracksQuery.data.tracks.length > 0 && (
          <div className="mb-8 space-y-2">
            <div className="text-xs text-neutral-300 mb-1">Master</div>
            <div className="mb-2">
              {(() => {
                try {
                  const post = (mix as any)?.getMasterAnalyser?.() as AnalyserNode | null;
                  return (
                    <MasterVuPro
                      postAnalyser={post}
                      height={12}
                      floorDb={-160}
                      ceilingDb={-0.1}
                      showScale={false}
                      showDbfs={true}
                      className="rounded w-full"
                    />
                  );
                } catch { return null; }
              })()}
            </div>
            <div className="mb-3">
              <div className="flex items-center gap-2">
                <span className="text-xs w-24 shrink-0 text-neutral-300">Master Level</span>
                {/* Right cluster: slider + readouts, aligned like stem rows. Percentage hidden on mobile. */}
                <div className="flex items-center gap-2 flex-1 min-w-[220px]">
                  <UiSlider
                    value={mix.masterVolume}
                    min={0}
                    max={Math.pow(10, 6 / 20)}
                    step={0.01}
                    onChange={mix.setMasterVolume}
                    className="flex-1"
                    aria-label="Master volume"
                  />
                  <span
                    className="text-[10px] w-12 text-right text-neutral-400 cursor-pointer hover:text-neutral-200"
                    title="Reset master level to 0 dB (100%)"
                    onClick={() => mix.setMasterVolume(1)}
                  >
                    {(() => {
                      try {
                        const refs: any = (mix as any)?._refs ?? (mix as any)?.refs ?? {};
                        const gate = Number(refs?.gateGainRef?.current?.gain?.value ?? 1);
                        const mg = Number(refs?.masterGainRef?.current?.gain?.value ?? mix.masterVolume ?? 1);
                        const g = Math.max(0, gate * mg);
                        const attDb = gainToDb(g);
                        return isFinite(attDb) ? `${attDb.toFixed(1)} dB` : '-∞ dB';
                      } catch { return '\u00A0'; }
                    })()}
                  </span>
                  <span
                    className="hidden md:inline text-[10px] w-10 text-right text-neutral-400 cursor-pointer hover:text-neutral-200"
                    title="Reset master level to 100%"
                    onClick={() => mix.setMasterVolume(1)}
                  >
                    {Math.round(mix.masterVolume*100)}%
                  </span>
                </div>
              </div>
            </div>
            <div className="mt-4 space-y-2">
              {tracksQuery.data.tracks.map(t => (
                <AudioStemRow
                  key={t.url}
                  role={t.role}
                  volume={mix.volumes[t.role] ?? 1}
                  muted={!!mix.muted[t.role]}
                  solo={!!mix.solo[t.role]}
                  levelPre={(() => {
                    const srcDb = mix.levels[t.role]?.srcDb;
                    return typeof srcDb === 'number' ? meterFromDb(srcDb, VU_FLOOR, VU_CEIL) : 0;
                  })()}
                  level={(() => {
                    const srcDb = mix.levels[t.role]?.srcDb;
                    if (typeof srcDb !== 'number') return 0;
                    const g = mix.getEffectiveGain(t.role);
                    if (g === 0) return 0;
                    const att = gainToDb(g);
                    const outDb = srcDb + att;
                    return meterFromDb(outDb, VU_FLOOR, VU_CEIL);
                  })()}
                  levelDbfs={(() => {
                    const srcDb = mix.levels[t.role]?.srcDb;
                    if (typeof srcDb !== 'number') return undefined;
                    const g = mix.getEffectiveGain(t.role);
                    if (g === 0) return srcDb;
                    const att = gainToDb(g);
                    return isFinite(att) ? srcDb + att : undefined;
                  })()}
                  attenuationDb={mix.levels[t.role]?.attenuationDb}
                  onVolumeChange={(v) => mix.setVolume(t.role, v)}
                  onToggleMute={() => mix.toggleMute(t.role)}
                  onToggleSolo={() => mix.toggleSolo(t.role)}
                />
              ))}
            </div>
          </div>
        )}

        {/* Transport row - sticky on small screens */}
        <div className={`flex items-center gap-3 mb-6 ${isSmall ? 'sticky bottom-0 z-10 pac-glass backdrop-blur  pt-2' : ''}`}>
          <MixTransport
            duration={mix.duration}
            // Use stabilized time to keep the seek bar coherent on resume
            currentTime={stableTime}
            status={mix.status}
            mediaRate={mix.masterPlaybackRate}
            onPlay={handlePlay}
            onPause={pause}
            onStop={stop}
            onSeek={seekAll}
          />
        </div>

        {/* Speed row */}
        <div className="flex items-center gap-2 mb-4">
          <span className="text-xs w-16 md:w-24 shrink-0 text-neutral-300">Speed</span>
          <div className="flex items-center gap-2 flex-1 min-w-[220px]">
            <UiSlider
              value={mix.maintainPitch ? uiSpeed : mix.masterPlaybackRate}
              min={0.5}
              max={2.0}
              step={0.01}
              onChange={(newRate) => {
                const safe = Math.max(0.5, Math.min(2.0, newRate));
                if (mix.maintainPitch) {
                  setUiSpeed(safe);
                  mix.setMasterPlaybackRate(safe);
                } else {
                  setUiSpeed(safe);
                  mix.setMasterPlaybackRate(safe);
                }
              }}
              aria-label="Playback speed"
              className="flex-1"
            />
            <span
              className="text-[10px] w-12 text-right text-neutral-400 cursor-pointer hover:text-neutral-200"
              title="Reset speed to 100%"
              onClick={() => {
                try { if (mix.maintainPitch) (mix as any).dsp?.pitch?.setSemitones?.(uiPitchSt); } catch {}
                setUiSpeed(1.0);
                mix.setMasterPlaybackRate(1.0);
                if (mix.maintainPitch) {
                  setTimeout(() => { try { (mix as any).dsp?.pitch?.setSemitones?.(uiPitchSt); } catch {} }, 0);
                }
              }}
            >
              {Math.round((mix.maintainPitch ? uiSpeed : mix.masterPlaybackRate) * 100)}%
            </span>
          </div>
          {/* Desktop/tablet: Vinyl Mode inline on the Speed row; hidden on phones */}
          <label className="hidden md:flex items-center gap-1 ml-3 text-[10px] text-neutral-300 select-none" title="When ON, speed affects pitch like a turntable">
            <input
              type="checkbox"
              className="accent-emerald-500"
              checked={!mix.maintainPitch}
              onChange={(e) => {
                const vinyl = e.target.checked;
                const rateToApply = mix.maintainPitch ? uiSpeed : mix.masterPlaybackRate;
                mix.setMaintainPitch(!vinyl);
                setUiPitchSt(0);
                try { (mix as any).dsp?.pitch?.setSemitones?.(0); } catch {}
                try {
                  const safe = Math.max(0.5, Math.min(2.0, rateToApply || 1.0));
                  setUiSpeed(safe);
                  mix.setMasterPlaybackRate(safe);
                } catch {}
              }}
            />
            Vinyl Mode
          </label>
        </div>

        {/* Pitch row */}
        <div className="flex items-center gap-2 mb-4">
          <span className="text-xs w-16 md:w-24 shrink-0 text-neutral-300">Pitch</span>
          <div className="flex items-center gap-2 flex-1 min-w-[220px]">
            <UiSlider
              value={uiPitchSt}
              min={-12}
              max={12}
              step={0.1}
              disabled={!mix.maintainPitch}
              onChange={(st) => {
                const clamped = Math.max(-12, Math.min(12, st));
                setUiPitchSt(clamped);
                try { (mix as any).dsp?.pitch?.setSemitones?.(clamped); } catch {}
              }}
              aria-label="Pitch shift (semitones)"
              className="flex-1"
            />
            <span
              className={`${mix.maintainPitch ? 'text-neutral-400 cursor-pointer hover:text-neutral-200' : 'text-neutral-600 cursor-not-allowed'} text-[10px] w-12 text-right`}
              title={mix.maintainPitch ? 'Reset pitch to 0 st' : 'Disabled in Vinyl Mode'}
              onClick={() => { if (!mix.maintainPitch) return; setUiPitchSt(0); try { (mix as any).dsp?.pitch?.setSemitones?.(0); } catch {} }}
            >
              {uiPitchSt.toFixed(1)} st
            </span>
          </div>
        </div>

        {/* Phone-only: Vinyl Mode as its own left-aligned row below Pitch */}
        {isSmall && (
          <div className="flex items-center mb-4">
            <label className="flex items-center gap-1 text-[10px] text-neutral-300 select-none" title="When ON, speed affects pitch like a turntable">
              <input
                type="checkbox"
                className="accent-emerald-500"
                checked={!mix.maintainPitch}
                onChange={(e) => {
                  const vinyl = e.target.checked;
                  const rateToApply = mix.maintainPitch ? uiSpeed : mix.masterPlaybackRate;
                  mix.setMaintainPitch(!vinyl);
                  setUiPitchSt(0);
                  try { (mix as any).dsp?.pitch?.setSemitones?.(0); } catch {}
                  try {
                    const safe = Math.max(0.5, Math.min(2.0, rateToApply || 1.0));
                    setUiSpeed(safe);
                    mix.setMasterPlaybackRate(safe);
                  } catch {}
                }}
              />
              Vinyl Mode
            </label>
          </div>
        )}

        {/* Waveform & Frequency Response */}
        <div className="w-full">
          <div className="mb-4">
            <div className="text-xs text-neutral-400 mb-1">Master Waveform</div>
            <div className="rounded-md border border-neutral-700/40 bg-black/60 overflow-hidden w-full">
              <WaveformPlot
                analyser={mix.getMasterAnalyser()}
                status={mix.status}
                width={700}
                height={160}
                fps={mix.dsp.waveform.fps}
                color="#9fffd8"
                background="#131313"
                showGrid={mix.dsp.waveform.grid}
                gridHorizLines={mix.dsp.waveform.horiz}
                gridVertLines={mix.dsp.waveform.vert}
              />
            </div>
          </div>
          <div className="mb-4">
            <div className="text-xs text-neutral-400 mb-1">Master Frequency Response</div>
            <div className="rounded-md border border-neutral-700/40 bg-black/60 overflow-hidden w-full">
              <MasterFrequencyResponse
                analyser={mix.getMasterAnalyser()}
                filter={mix.getMasterFilter()}
                highpass={mix.getMasterHighpassFilter()}
                eqEnabled={mix.dsp.eq}
                eqHpfEnabled={mix.dsp.eqHpf}
                eqLpfEnabled={mix.dsp.eqLpf}
                status={mix.status}
                width={600}
                height={160}
                cutoffHz={mix.masterLowpassHz}
                onCutoffChange={mix.setMasterLowpassHz}
                hpfHz={mix.masterHighpassHz}
                onHpfChange={mix.setMasterHighpassHz}
              />
            </div>
          </div>
  </div>
      </div>
  );

  return (
    <div className="flex flex-col md:flex-row gap-6 items-stretch h-full min-h-0">
      {/* Mobile tab switcher */}
      {isSmall && (
        <div className="md:hidden -mt-1 mb-2 px-1">
          <div className="flex w-full rounded-md overflow-hidden border border-neutral-700/50 pac-glass text-[11px]">
            <button
              className={`flex-1 px-3 py-1 ${activeTab==='library' ? 'bg-neutral-700/50 text-neutral-100' : 'text-neutral-300 hover:text-neutral-100'}`}
              onClick={() => setActiveTab('library')}
            >Library</button>
            <button
              className={`flex-1 px-3 py-1 ${activeTab==='player' ? 'bg-neutral-700/50 text-neutral-100' : 'text-neutral-300 hover:text-neutral-100'}`}
              onClick={() => setActiveTab('player')}
            >Player</button>
          </div>
        </div>
      )}

      {/* Library and Player panes */}
      {isSmall ? (
        <div className="w-full px-1 flex-1 min-h-0 flex flex-col">
          {activeTab === 'library' ? LibraryFrame : PlayerFrame}
        </div>
      ) : (
        <>
          {LibraryFrame}
          {PlayerFrame}
        </>
      )}
    </div>
  );
};

export default AudioLibraryPlayer;

